import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/appIcon';
import Button from '../../../components/ui/Button';

const CARD =
    'bg-gray-900/80 border border-gray-800/90 rounded-2xl shadow-inner shadow-black/40';
const PANEL =
    'bg-gradient-to-b from-gray-950 via-gray-950/95 to-gray-900 border border-gray-800/90 rounded-2xl shadow-[0_25px_80px_rgba(0,0,0,0.65)]';

const TONE = {
    success: { iconWrap: 'bg-emerald-500/15 border border-emerald-400/40', icon: 'text-emerald-300', text: 'text-emerald-300' },
    warning: { iconWrap: 'bg-amber-500/15 border border-amber-400/40', icon: 'text-amber-300', text: 'text-amber-300' },
    primary: { iconWrap: 'bg-cyan-500/15 border border-cyan-400/40', icon: 'text-cyan-300', text: 'text-cyan-300' },
    error: { iconWrap: 'bg-red-500/15 border border-red-400/40', icon: 'text-red-300', text: 'text-red-300' }
};

const EmptyState = ({ title = 'Sin datos', desc = 'Ajusta el rango o carga información.' }) => (
    <div className={CARD + ' p-6 flex items-center justify-center'}>
        <div className="text-center max-w-md">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mb-3">
                <Icon name="Database" size={22} className="text-cyan-300" />
            </div>
            <p className="text-white font-semibold">{title}</p>
            <p className="text-sm text-gray-400 mt-1">{desc}</p>
        </div>
    </div>
);

const safeNumber = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

/**
 * comparativeData esperado:
 * {
 *   monthly: [{ period:'2025-01'|'Ene'|..., current:number, previous:number }],
 *   yearly:  [{ period:'2024'|..., current:number, previous:number }]
 * }
 *
 * También soporta tu formato viejo si viene:
 * monthly: [{ month, current, previous, growth }]
 * yearly:  [{ year, revenue, profit, margin }]  <-- (NO recomendado para "comparative" de revenue)
 */
const ComparativeReports = ({
    dateRange,
    onDateRangeChange,
    onExport,

    comparativeData, // ✅ datos reales
    loading = false,
    error = null,

    locale = 'es-MX',
    defaultCurrency = 'MXN'
}) => {
    const [comparisonType, setComparisonType] = useState('monthly');
    const [currency, setCurrency] = useState(defaultCurrency);

    const formatCurrency = (value) =>
        new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            minimumFractionDigits: 0
        }).format(safeNumber(value));

    const rows = useMemo(() => {
        const raw = comparativeData?.[comparisonType] ?? [];
        // Normalizamos a un shape único:
        return raw.map((r) => {
            // Soportar variantes:
            const period = r.period ?? r.month ?? r.year ?? '—';

            // Caso recomendado: current/previous
            const current = r.current != null ? safeNumber(r.current) : safeNumber(r.revenue);
            const previous =
                r.previous != null
                    ? safeNumber(r.previous)
                    : r.revenue != null
                        ? safeNumber(r.revenue) - safeNumber(r.delta ?? 0)
                        : safeNumber(r.previous);

            const delta = current - previous;
            const growth =
                r.growth != null
                    ? safeNumber(r.growth)
                    : previous !== 0
                        ? (delta / previous) * 100
                        : 0;

            return { period, current, previous, delta, growth };
        });
    }, [comparativeData, comparisonType]);

    const metrics = useMemo(() => {
        if (!rows.length) return null;

        const avgGrowth = rows.reduce((acc, r) => acc + safeNumber(r.growth), 0) / rows.length;

        const best = rows.reduce((max, r) => (r.growth > max.growth ? r : max), rows[0]);
        const maxGrowth = Math.max(...rows.map((r) => r.growth));
        const minGrowth = Math.min(...rows.map((r) => r.growth));

        return {
            avgGrowth,
            bestPeriod: best.period,
            maxGrowth,
            minGrowth
        };
    }, [rows]);

    if (loading) {
        return (
            <div className="space-y-4">
                <div className={PANEL + ' p-5'}>
                    <div className="h-5 w-56 bg-gray-800/70 rounded-lg" />
                    <div className="h-4 w-72 bg-gray-900/70 rounded-lg mt-2" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[0, 1, 2, 3].map((i) => (
                        <div key={i} className={CARD + ' p-5'}>
                            <div className="h-11 w-11 bg-gray-800/60 rounded-xl mb-3" />
                            <div className="h-7 w-40 bg-gray-800/60 rounded-lg" />
                            <div className="h-4 w-32 bg-gray-900/60 rounded-lg mt-2" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) return <EmptyState title="Error al cargar" desc={String(error)} />;

    if (!comparativeData || rows.length === 0) {
        return <EmptyState title="Comparativo sin datos" desc="No hay información para este rango." />;
    }

    const kpis = [
        {
            label: 'Crecimiento Promedio',
            value: metrics ? metrics.avgGrowth.toFixed(1) + '%' : '—',
            icon: 'TrendingUp',
            tone: 'success'
        },
        {
            label: comparisonType === 'monthly' ? 'Mejor Mes' : 'Mejor Año',
            value: metrics ? metrics.bestPeriod : '—',
            icon: 'Award',
            tone: 'primary'
        },
        {
            label: 'Crecimiento Máximo',
            value: metrics ? metrics.maxGrowth.toFixed(1) + '%' : '—',
            icon: 'Maximize',
            tone: 'primary'
        },
        {
            label: 'Crecimiento Mínimo',
            value: metrics ? metrics.minGrowth.toFixed(1) + '%' : '—',
            icon: 'Minimize',
            tone: 'warning'
        }
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className={PANEL + ' p-4 sm:p-5'}>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg sm:text-xl font-bold text-white truncate">Reportes Comparativos</h3>
                            <span className="inline-flex text-[11px] font-mono px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/40">
                                {dateRange?.start || '—'} → {dateRange?.end || '—'}
                            </span>
                        </div>
                        <p className="text-cyan-200/80 text-xs sm:text-sm truncate">
                            Tendencias y variaciones
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-2 justify-end">
                        {/* Toggle */}
                        <div className="inline-flex items-center gap-1 bg-gray-950/50 border border-gray-800 rounded-xl p-1">
                            {[
                                { id: 'monthly', label: 'Mensual', icon: 'Calendar' },
                                { id: 'yearly', label: 'Anual', icon: 'CalendarDays' }
                            ].map((t) => {
                                const active = comparisonType === t.id;
                                return (
                                    <button
                                        key={t.id}
                                        onClick={() => setComparisonType(t.id)}
                                        className={[
                                            'px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all flex items-center gap-2',
                                            active
                                                ? 'bg-cyan-500/15 border border-cyan-500/40 text-cyan-200 shadow-[0_0_18px_rgba(34,211,238,0.25)]'
                                                : 'text-gray-300 hover:text-white hover:bg-gray-900/70'
                                        ].join(' ')}
                                    >
                                        <Icon name={t.icon} className="w-4 h-4" />
                                        {t.label}
                                    </button>
                                );
                            })}
                        </div>

                        <select
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                            className="bg-gray-950/50 border border-gray-800 rounded-xl px-3 py-2 text-xs sm:text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                        >
                            <option value="MXN">MXN $</option>
                            <option value="USD">USD $</option>
                            <option value="ARS">ARS $</option>
                        </select>

                        <Button
                            variant="outline"
                            onClick={() => onExport?.('comparative')}
                            className="border-gray-700 text-gray-300 hover:bg-gray-900 hover:text-white"
                        >
                            <Icon name="Download" className="w-4 h-4 mr-2" />
                            Exportar
                        </Button>
                    </div>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {kpis.map((m, i) => {
                    const st = TONE[m.tone] ?? TONE.primary;
                    return (
                        <motion.div
                            key={m.label}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.06 }}
                            className={CARD + ' p-4 sm:p-5'}
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${st.iconWrap}`}>
                                    <Icon name={m.icon} size={20} className={st.icon} />
                                </div>
                            </div>

                            <p className="text-xl sm:text-2xl font-bold text-white mb-1 font-mono">{m.value}</p>
                            <p className="text-xs sm:text-sm text-gray-400">{m.label}</p>
                        </motion.div>
                    );
                })}
            </div>

            {/* Tabla */}
            <div className={CARD + ' p-4 sm:p-6'}>
                <div className="flex items-center justify-between gap-3 mb-4">
                    <div>
                        <h4 className="text-sm sm:text-base font-semibold text-white">
                            {comparisonType === 'monthly' ? 'Comparación Mensual' : 'Comparación Anual'}
                        </h4>
                        <p className="text-[11px] text-gray-400">Actual vs anterior • variación y crecimiento</p>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full min-w-[860px]">
                        <thead>
                            <tr className="border-b border-gray-800/80">
                                <th className="text-left py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                    {comparisonType === 'monthly' ? 'Periodo' : 'Periodo'}
                                </th>
                                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                    Actual
                                </th>
                                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                    Anterior
                                </th>
                                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                    Variación
                                </th>
                                <th className="text-right py-3 px-4 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                    Crecimiento
                                </th>
                            </tr>
                        </thead>

                        <tbody>
                            {rows.map((r, idx) => {
                                const growthTone = r.growth >= 0 ? 'success' : 'error';
                                const st = TONE[growthTone];
                                return (
                                    <tr key={`${r.period}-${idx}`} className="border-b border-gray-800/50 last:border-0 hover:bg-gray-950/40">
                                        <td className="py-3 px-4 text-sm font-semibold text-gray-100">
                                            {r.period}
                                        </td>
                                        <td className="py-3 px-4 text-sm text-right text-gray-100 font-mono">
                                            {formatCurrency(r.current)}
                                        </td>
                                        <td className="py-3 px-4 text-sm text-right text-gray-400 font-mono">
                                            {formatCurrency(r.previous)}
                                        </td>
                                        <td className="py-3 px-4 text-sm text-right text-gray-100 font-mono">
                                            {formatCurrency(r.delta)}
                                        </td>
                                        <td className="py-3 px-4 text-sm text-right">
                                            <span className={`inline-flex items-center gap-1 font-mono font-semibold ${st.text}`}>
                                                <Icon name={r.growth >= 0 ? 'TrendingUp' : 'TrendingDown'} size={12} />
                                                {r.growth.toFixed(1)}%
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Insights (sin inventar): mostramos top/bottom reales */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className={CARD + ' p-4 sm:p-6'}>
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-emerald-500/15 border border-emerald-400/40">
                            <Icon name="CheckCircle" size={18} className="text-emerald-300" />
                        </div>
                        <div>
                            <h4 className="text-sm sm:text-base font-semibold text-white">Mejores periodos</h4>
                            <p className="text-[11px] text-gray-400">Top 4 por crecimiento</p>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {rows
                            .slice()
                            .sort((a, b) => b.growth - a.growth)
                            .slice(0, 4)
                            .map((r, i) => (
                                <div key={`${r.period}-top-${i}`} className="flex items-center justify-between p-3 bg-gray-950/60 border border-gray-800 rounded-2xl">
                                    <div>
                                        <p className="text-sm font-semibold text-gray-100">{r.period}</p>
                                        <p className="text-[11px] text-gray-400">Δ {formatCurrency(r.delta)}</p>
                                    </div>
                                    <span className="text-emerald-300 font-mono font-bold">{r.growth.toFixed(1)}%</span>
                                </div>
                            ))}
                    </div>
                </div>

                <div className={CARD + ' p-4 sm:p-6'}>
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-amber-500/15 border border-amber-400/40">
                            <Icon name="Lightbulb" size={18} className="text-amber-300" />
                        </div>
                        <div>
                            <h4 className="text-sm sm:text-base font-semibold text-white">Periodos a revisar</h4>
                            <p className="text-[11px] text-gray-400">Bottom 4 por crecimiento</p>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {rows
                            .slice()
                            .sort((a, b) => a.growth - b.growth)
                            .slice(0, 4)
                            .map((r, i) => (
                                <div key={`${r.period}-low-${i}`} className="flex items-center justify-between p-3 bg-gray-950/60 border border-gray-800 rounded-2xl">
                                    <div>
                                        <p className="text-sm font-semibold text-gray-100">{r.period}</p>
                                        <p className="text-[11px] text-gray-400">Δ {formatCurrency(r.delta)}</p>
                                    </div>
                                    <span className="text-amber-300 font-mono font-bold">{r.growth.toFixed(1)}%</span>
                                </div>
                            ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ComparativeReports;
